// ===========================================
// Admin Event Detail Page
// ===========================================
// Shows full event info + status actions, tabbed sub-panels for RSVPs,
// Registrations, Attendance, Tickets, and Photos.
// Route: events/:id

import { useState, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  Grid,
  Skeleton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Cancel as CancelIcon,
  CheckCircle as CompleteIcon,
  PersonAdd as ConvertIcon,
  QrCode as QrIcon,
  CameraAlt as CameraIcon,
  ArrowBack as BackIcon,
  EditLocationAlt as AdjustPinIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  useEvent,
  usePublishEvent,
  useCancelEvent,
  useCompleteEvent,
  useDeleteEvent,
  useEventRsvps,
  useEventRsvpStats,
  useUpdateRsvpStatus,
  useEventRegistrations,
  useConvertRegistration,
  useEventAttendance,
  useCheckIn,
  useEventTickets,
} from '../../hooks/useEvents';
import {
  EventStatus,
  RSVPResponse,
  RSVPWithRelations,
  EventRegistration,
  EventAttendance,
  EventTicket,
} from '../../api/events.api';
import PageHeader from '../../components/common/PageHeader';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import EventPhotosPanel from '../../components/admin/EventPhotosPanel';
import { tierPriceSummary, TierPriceList, formatCents } from '../../components/events/EventTierPricing';
import { formatAddress } from '../../constants/countries';

// Code-split the venue map (a static Mapbox image) so it only loads on the
// detail view, not in the main admin bundle.
const VenueMap = lazy(() => import('../../components/events/VenueMap'));
const AdjustPinModal = lazy(() => import('../../components/events/AdjustPinModal'));
// Lazy — pulls in @zxing (large) only when an admin opens the camera scanner,
// keeping it out of the main bundle (which the PWA precache caps at 2 MiB).
const QrScannerModal = lazy(() => import('../../components/events/QrScannerModal'));

// ===========================================
// STATUS CONFIG
// ===========================================

const statusConfig: Record<EventStatus, { label: string; bgcolor: string; color: string }> = {
  DRAFT: { label: 'Draft', bgcolor: '#9DA5AF', color: 'white' },
  PUBLISHED: { label: 'Published', bgcolor: '#4580E4', color: 'white' },
  CANCELLED: { label: 'Cancelled', bgcolor: '#ff4040', color: 'white' },
  COMPLETED: { label: 'Completed', bgcolor: '#96DAC9', color: '#080808' },
  ARCHIVED: { label: 'Archived', bgcolor: '#FFCE00', color: '#080808' },
};

const rsvpResponseConfig: Record<RSVPResponse, { label: string; bgcolor: string; color: string }> = {
  ATTENDING: { label: 'Attending', bgcolor: '#96DAC9', color: '#080808' },
  NOT_ATTENDING: { label: 'Not Attending', bgcolor: '#ff4040', color: 'white' },
  MAYBE: { label: 'Maybe', bgcolor: '#FFCE00', color: '#080808' },
  PENDING: { label: 'Pending', bgcolor: '#9DA5AF', color: 'white' },
  WAITLIST: { label: 'Waitlist', bgcolor: '#FFAE9E', color: '#080808' },
};

// ===========================================
// TAB PANEL HELPER
// ===========================================

interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, index, value }: TabPanelProps) {
  return (
    <Box hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

// ===========================================
// CANCEL REASON DIALOG
// ===========================================

interface CancelDialogProps {
  open: boolean;
  loading: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

function CancelEventDialog({ open, loading, onConfirm, onCancel }: CancelDialogProps) {
  const [reason, setReason] = useState('');
  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Cancel Event</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Provide an optional reason for cancellation. This may be shown to attendees.
        </DialogContentText>
        <TextField
          autoFocus
          label="Reason (optional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={2}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Back
        </Button>
        <Button
          onClick={() => onConfirm(reason)}
          color="error"
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : undefined}
        >
          Cancel Event
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ===========================================
// RSVPs TAB
// ===========================================

function RsvpsTab({ eventId }: { eventId: string }) {
  const { data: rsvpsRes, isLoading } = useEventRsvps(eventId);
  const { data: statsRes } = useEventRsvpStats(eventId);
  const updateRsvp = useUpdateRsvpStatus();

  const rsvps: RSVPWithRelations[] = rsvpsRes?.data ?? [];
  const stats = statsRes;

  return (
    <Box>
      {stats && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Attending', value: stats.attending, color: '#96DAC9' },
            { label: 'Not Attending', value: stats.notAttending, color: '#ff4040' },
            { label: 'Maybe', value: stats.maybe, color: '#FFCE00' },
            { label: 'Waitlist', value: stats.waitlist, color: '#FFAE9E' },
            { label: 'Total Guests', value: stats.totalGuests, color: '#4580E4' },
          ].map((item) => (
            <Grid item xs={6} sm={4} md={2} key={item.label}>
              <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: 'background.paper' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color: item.color }}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {item.label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Guests</TableCell>
              <TableCell>Response</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Promote</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(4)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(6)].map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : rsvps.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No RSVPs yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              rsvps.map((rsvp) => {
                const cfg = rsvpResponseConfig[rsvp.response];
                const name = rsvp.Family?.name
                  ?? (rsvp.Student
                    ? `${rsvp.Student.firstName} ${rsvp.Student.lastName}`
                    : '—');
                return (
                  <TableRow key={rsvp.id} hover>
                    <TableCell>
                      <Typography variant="body2">{name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{rsvp.guestCount}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={cfg.label}
                        size="small"
                        sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {rsvp.notes ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {format(new Date(rsvp.createdAt), 'dd MMM yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {rsvp.response === 'WAITLIST' && (
                        <Tooltip title="Promote to Attending">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={updateRsvp.isPending}
                            onClick={() =>
                              updateRsvp.mutate({
                                eventId,
                                rsvpId: rsvp.id,
                                response: 'ATTENDING',
                              })
                            }
                          >
                            Promote
                          </Button>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ===========================================
// REGISTRATIONS TAB
// ===========================================

function RegistrationsTab({ eventId }: { eventId: string }) {
  const { data: regsRes, isLoading } = useEventRegistrations(eventId);
  const convertMutation = useConvertRegistration();
  const [confirmConvertId, setConfirmConvertId] = useState<string | null>(null);

  const registrations: EventRegistration[] = regsRes?.data ?? [];

  const handleConvertConfirm = async () => {
    if (!confirmConvertId) return;
    await convertMutation.mutateAsync({ eventId, registrationId: confirmConvertId });
    setConfirmConvertId(null);
  };

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Guests</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Converted</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(4)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(8)].map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : registrations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No registrations yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              registrations.map((reg) => (
                <TableRow key={reg.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {reg.firstName} {reg.lastName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{reg.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {reg.phone ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{reg.guestCount}</Typography>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const cfg = rsvpResponseConfig[reg.status];
                      return (
                        <Chip
                          label={cfg.label}
                          size="small"
                          sx={{ bgcolor: cfg.bgcolor, color: cfg.color, fontWeight: 600 }}
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {format(new Date(reg.createdAt), 'dd MMM yyyy')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {reg.convertedToFamilyId ? (
                      <Chip label="Converted" size="small" color="success" variant="outlined" />
                    ) : (
                      <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {!reg.convertedToFamilyId && (
                      <Tooltip title="Convert to Family Account">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setConfirmConvertId(reg.id)}
                        >
                          <ConvertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={!!confirmConvertId}
        title="Convert Registration"
        message="Convert this registration into a family account? This creates a login for the family and emails them a link to set their password."
        confirmLabel="Convert"
        loading={convertMutation.isPending}
        onConfirm={handleConvertConfirm}
        onCancel={() => setConfirmConvertId(null)}
      />
    </Box>
  );
}

// ===========================================
// ATTENDANCE TAB
// ===========================================

function AttendanceTab({ eventId }: { eventId: string }) {
  const { data: attendanceRes, isLoading } = useEventAttendance(eventId);
  const checkInMutation = useCheckIn();
  const [qrToken, setQrToken] = useState('');
  const [checkInError, setCheckInError] = useState<string | null>(null);
  const [checkInSuccess, setCheckInSuccess] = useState<string | null>(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  const attendance: EventAttendance[] = attendanceRes ?? [];

  // Shared check-in for both manual entry and camera scan. Returns void so it
  // can be passed straight to the scanner's onScan callback.
  const submitCheckIn = async (token: string) => {
    const trimmed = token.trim();
    if (!trimmed) return;
    setCheckInError(null);
    setCheckInSuccess(null);
    try {
      const result = await checkInMutation.mutateAsync({ eventId, qrToken: trimmed });
      setCheckInSuccess(
        result.alreadyCheckedIn
          ? `${result.holderName} was already checked in`
          : `${result.holderName} checked in successfully`
      );
      setQrToken('');
    } catch {
      setCheckInError('Check-in failed. Invalid or unknown token.');
    }
  };

  const handleCheckIn = (e: React.FormEvent) => {
    e.preventDefault();
    void submitCheckIn(qrToken);
  };

  return (
    <Box>
      {/* Check-in input */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
          QR Check-In
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Scan an attendee's ticket with the camera, or enter the QR token manually.
        </Typography>
        <Box component="form" onSubmit={handleCheckIn}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              startIcon={<CameraIcon />}
              onClick={() => setScannerOpen(true)}
            >
              Scan with camera
            </Button>
            <TextField
              size="small"
              label="QR Token"
              value={qrToken}
              onChange={(e) => setQrToken(e.target.value)}
              sx={{ flex: 1, minWidth: 200, maxWidth: 360 }}
              InputProps={{
                startAdornment: (
                  <QrIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                ),
              }}
            />
            <Button
              type="submit"
              variant="outlined"
              disabled={!qrToken.trim() || checkInMutation.isPending}
              startIcon={checkInMutation.isPending ? <CircularProgress size={14} /> : undefined}
            >
              Check In
            </Button>
          </Stack>
        </Box>
        {checkInSuccess && (
          <Alert severity="success" sx={{ mt: 1.5 }} onClose={() => setCheckInSuccess(null)}>
            {checkInSuccess}
          </Alert>
        )}
        {checkInError && (
          <Alert severity="error" sx={{ mt: 1.5 }} onClose={() => setCheckInError(null)}>
            {checkInError}
          </Alert>
        )}
      </Paper>

      {scannerOpen && (
        <Suspense fallback={null}>
          <QrScannerModal
            open={scannerOpen}
            onClose={() => setScannerOpen(false)}
            onScan={(token) => void submitCheckIn(token)}
          />
        </Suspense>
      )}

      {/* Checked-in attendees. Event attendance is binary — an attendee is
          checked in (scanned at the door) or they aren't on the list. The
          lesson-style Present/Absent/Late/Excused vocabulary doesn't apply. */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        Checked In ({attendance.length})
      </Typography>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Attendee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Checked in</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              [...Array(4)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(3)].map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : attendance.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No one checked in yet</Typography>
                </TableCell>
              </TableRow>
            ) : (
              attendance.map((rec) => {
                // PRESENT (the value QR check-in writes) = checked in. Any other
                // legacy status counts as not-yet-admitted.
                const checkedIn = rec.status === 'PRESENT';
                return (
                  <TableRow key={rec.id} hover>
                    <TableCell>
                      <Typography variant="body2">
                        {rec.Student
                          ? `${rec.Student.firstName} ${rec.Student.lastName}`
                          : rec.EventTicket
                            ? rec.EventTicket.holderName
                            : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={checkedIn ? 'Checked in' : 'Not checked in'}
                        size="small"
                        icon={checkedIn ? <CompleteIcon /> : undefined}
                        sx={{
                          bgcolor: checkedIn ? '#96DAC9' : '#9DA5AF',
                          color: checkedIn ? '#080808' : 'white',
                          fontWeight: 600,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {checkedIn ? format(new Date(rec.createdAt), 'dd MMM yyyy, h:mm a') : '—'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

// ===========================================
// TICKETS TAB
// ===========================================

function TicketsTab({ eventId }: { eventId: string }) {
  const { data: ticketsRes, isLoading } = useEventTickets(eventId);
  const tickets: EventTicket[] = ticketsRes ?? [];

  // Keys match the Prisma TicketStatus enum (PAID/CHECKED_IN are the live
  // values; legacy CONFIRMED/USED never existed and left tickets uncoloured).
  const ticketStatusColors: Record<string, string> = {
    PENDING: '#9DA5AF',
    PAID: '#4580E4',
    CHECKED_IN: '#96DAC9',
    CANCELLED: '#ff4040',
    REFUNDED: '#FFAE9E',
  };

  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Holder</TableCell>
            <TableCell>Email</TableCell>
            <TableCell>Price</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Checked In</TableCell>
            <TableCell>Created</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            [...Array(4)].map((_, i) => (
              <TableRow key={i}>
                {[...Array(6)].map((__, j) => (
                  <TableCell key={j}><Skeleton variant="text" /></TableCell>
                ))}
              </TableRow>
            ))
          ) : tickets.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No tickets yet</Typography>
              </TableCell>
            </TableRow>
          ) : (
            tickets.map((ticket) => (
              <TableRow key={ticket.id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {ticket.holderName}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.holderEmail}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{formatCents(ticket.priceCents)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={ticket.status}
                    size="small"
                    sx={{
                      bgcolor: ticketStatusColors[ticket.status] ?? '#9DA5AF',
                      color: ticket.status === 'PENDING' || ticket.status === 'REFUNDED'
                        ? '#080808'
                        : 'white',
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.checkedInAt
                      ? format(new Date(ticket.checkedInAt), 'dd MMM yyyy, h:mm a')
                      : '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {format(new Date(ticket.createdAt), 'dd MMM yyyy')}
                  </Typography>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ===========================================
// MAIN COMPONENT
// ===========================================

export default function EventDetailPage() {
  const navigate = useNavigate();
  const { schoolSlug, id } = useParams<{ schoolSlug: string; id: string }>();

  const [activeTab, setActiveTab] = useState(0);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [pinModalOpen, setPinModalOpen] = useState(false);

  const { data: event, isLoading, isError } = useEvent(id ?? '');

  const publishMutation = usePublishEvent();
  const cancelMutation = useCancelEvent();
  const completeMutation = useCompleteEvent();
  const deleteMutation = useDeleteEvent();

  if (isLoading) {
    return (
      <Box sx={{ py: 4 }}>
        <Skeleton variant="text" width="40%" height={48} />
        <Skeleton variant="rectangular" height={160} sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (isError || !event) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load event. It may have been deleted.
      </Alert>
    );
  }

  const statusCfg = statusConfig[event.status];

  // The pin-adjust target: whichever of Venue/Location the event uses and that
  // has coords to adjust (one-shape rule → at most one is set).
  const pinTarget: { target: 'venue' | 'location'; id: string; name: string; latitude: number; longitude: number } | null =
    event.Venue && event.Venue.latitude != null && event.Venue.longitude != null
      ? { target: 'venue', id: event.Venue.id, name: event.Venue.name, latitude: event.Venue.latitude, longitude: event.Venue.longitude }
      : event.Location && event.Location.latitude != null && event.Location.longitude != null
        ? { target: 'location', id: event.Location.id, name: event.Location.name, latitude: event.Location.latitude, longitude: event.Location.longitude }
        : null;

  const handlePublish = async () => {
    await publishMutation.mutateAsync(event.id);
  };

  const handleCancel = async (reason: string) => {
    await cancelMutation.mutateAsync({ id: event.id, reason: reason || undefined });
    setCancelDialogOpen(false);
  };

  const handleComplete = async () => {
    await completeMutation.mutateAsync(event.id);
    setCompleteDialogOpen(false);
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(event.id);
    navigate(`/${schoolSlug}/admin/events`);
  };

  return (
    <Box>
      <PageHeader
        title={event.title}
        breadcrumbs={[
          { label: 'Events', path: '/admin/events' },
          { label: event.title },
        ]}
        action={
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {event.status === 'DRAFT' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  publishMutation.isPending ? <CircularProgress size={14} /> : <PublishIcon />
                }
                disabled={publishMutation.isPending}
                onClick={handlePublish}
              >
                Publish
              </Button>
            )}
            {event.status === 'PUBLISHED' && (
              <>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<CompleteIcon />}
                  onClick={() => setCompleteDialogOpen(true)}
                >
                  Complete
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<CancelIcon />}
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Cancel
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => navigate(`/${schoolSlug}/admin/events/${event.id}/edit`)}
            >
              Edit
            </Button>
            {(event.status === 'DRAFT' || event.status === 'CANCELLED') && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
              >
                Delete
              </Button>
            )}
            <Button
              variant="text"
              startIcon={<BackIcon />}
              onClick={() => navigate(`/${schoolSlug}/admin/events`)}
            >
              Back
            </Button>
          </Stack>
        }
      />

      {/* Event Summary Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          {event.featuredImage && (
            <Box
              component="img"
              src={event.featuredImage}
              alt={event.title}
              sx={{
                width: { xs: '100%', sm: 200 },
                height: 140,
                objectFit: 'cover',
                borderRadius: 1,
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <Chip
                label={statusCfg.label}
                sx={{ bgcolor: statusCfg.bgcolor, color: statusCfg.color, fontWeight: 700 }}
              />
              <Chip label={event.type.replace(/_/g, ' ')} variant="outlined" size="small" />
              {event.isFree ? (
                <Chip label="Free" size="small" color="success" variant="outlined" />
              ) : (
                <Chip
                  label={
                    tierPriceSummary(event.EventTicketTier) ??
                    `$${((event.ticketPriceCents ?? 0) / 100).toFixed(2)}`
                  }
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>

            <Stack spacing={0.5}>
              <Typography variant="body2">
                <strong>Start:</strong>{' '}
                {format(new Date(event.startDateTime), 'EEEE d MMMM yyyy, h:mm a')}
              </Typography>
              <Typography variant="body2">
                <strong>End:</strong>{' '}
                {format(new Date(event.endDateTime), 'EEEE d MMMM yyyy, h:mm a')}
              </Typography>
              {(event.Venue?.name || event.venueName || event.Location?.name) && (
                <Typography variant="body2">
                  <strong>Venue:</strong>{' '}
                  {event.Venue?.name || event.venueName || event.Location?.name}
                  {event.Room?.name ? ` — ${event.Room.name}` : ''}
                </Typography>
              )}
              {(() => {
                // Prefer an external Venue's address, else a school Location's.
                const addr = event.Venue ?? event.Location ?? null;
                const addressText = addr
                  ? formatAddress({ ...addr, country: addr.country ?? '' })
                  : null;
                return addressText ? (
                  <Typography variant="body2">
                    <strong>Address:</strong> {addressText}
                  </Typography>
                ) : null;
              })()}
              {event.capacity != null && (
                <Typography variant="body2">
                  <strong>Capacity:</strong> {event.capacity}
                </Typography>
              )}
              {event.registrationDeadline && (
                <Typography variant="body2">
                  <strong>Registration Deadline:</strong>{' '}
                  {format(new Date(event.registrationDeadline), 'dd MMM yyyy, h:mm a')}
                </Typography>
              )}
              {!event.isFree && event.EventTicketTier && event.EventTicketTier.length > 0 && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Ticket prices:</strong>
                  </Typography>
                  <TierPriceList tiers={event.EventTicketTier} dense />
                </Box>
              )}
            </Stack>

            {event.description && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {event.description}
                </Typography>
              </>
            )}
          </Box>
        </Stack>

        {/* Venue map (lazy) — renders a real map when the venue is geocoded,
            else degrades to a maps link inside VenueMap. */}
        {event.Venue && (
          <Box sx={{ mt: 2 }}>
            <Suspense fallback={null}>
              <VenueMap
                latitude={event.Venue.latitude}
                longitude={event.Venue.longitude}
                name={event.Venue.name}
                address={formatAddress(event.Venue)}
              />
            </Suspense>
            {event.Venue.latitude != null && event.Venue.longitude != null && (
              <Button
                size="small"
                startIcon={<AdjustPinIcon />}
                onClick={() => setPinModalOpen(true)}
                sx={{ mt: 1 }}
              >
                Adjust pin
              </Button>
            )}
          </Box>
        )}

        {/* School Location map (when the event uses a Location, not an external
            Venue — one-shape rule means only one is ever set). */}
        {!event.Venue && event.Location && (
          <Box sx={{ mt: 2 }}>
            <Suspense fallback={null}>
              <VenueMap
                latitude={event.Location.latitude}
                longitude={event.Location.longitude}
                name={event.Location.name}
                address={formatAddress({ ...event.Location, country: event.Location.country ?? '' })}
              />
            </Suspense>
            {event.Location.latitude != null && event.Location.longitude != null && (
              <Button
                size="small"
                startIcon={<AdjustPinIcon />}
                onClick={() => setPinModalOpen(true)}
                sx={{ mt: 1 }}
              >
                Adjust pin
              </Button>
            )}
          </Box>
        )}

        {/* Count chips */}
        {event._count && (
          <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap">
            <Chip
              label={`${event._count.EventRSVP} RSVPs`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${event._count.EventRegistration} Registrations`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${event._count.EventTicket} Tickets`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${event._count.EventAttendance} Attendance`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${event._count.EventPhoto} Photos`}
              size="small"
              variant="outlined"
            />
          </Stack>
        )}
      </Paper>

      {/* Tabbed Sub-Panels */}
      <Paper>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="RSVPs" />
          <Tab label="Registrations" />
          <Tab label="Attendance" />
          <Tab label="Tickets" />
          <Tab label="Photos" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <RsvpsTab eventId={event.id} />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <RegistrationsTab eventId={event.id} />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <AttendanceTab eventId={event.id} />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <TicketsTab eventId={event.id} />
          </TabPanel>
          <TabPanel value={activeTab} index={4}>
            <EventPhotosPanel eventId={event.id} />
          </TabPanel>
        </Box>
      </Paper>

      {/* Dialogs */}
      {/* Adjust-pin modal targets whichever of Venue/Location the event uses
          (one-shape rule → only one is ever set + geocoded). */}
      {pinTarget && (
        <Suspense fallback={null}>
          <AdjustPinModal
            open={pinModalOpen}
            onClose={() => setPinModalOpen(false)}
            target={pinTarget.target}
            targetId={pinTarget.id}
            eventId={event.id}
            name={pinTarget.name}
            latitude={pinTarget.latitude}
            longitude={pinTarget.longitude}
          />
        </Suspense>
      )}

      <CancelEventDialog
        open={cancelDialogOpen}
        loading={cancelMutation.isPending}
        onConfirm={handleCancel}
        onCancel={() => setCancelDialogOpen(false)}
      />

      <ConfirmDialog
        open={completeDialogOpen}
        title="Mark Event as Completed"
        message="Mark this event as completed? Attendees will no longer be able to RSVP or purchase tickets."
        confirmLabel="Complete"
        confirmColor="success"
        loading={completeMutation.isPending}
        onConfirm={handleComplete}
        onCancel={() => setCompleteDialogOpen(false)}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Event"
        message="Are you sure you want to permanently delete this event? This cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
      />
    </Box>
  );
}
